/**
 * MATCH TRANSITIONS — the doors intro and the goal wipe (owner spec: "2 team
 * names on a door slide in from left and right and VS in middle, when the
 * doors slam a shake, and doors open to a game"). Broadcast energy, pure
 * DOM/CSS like ui/postMatch.ts — nothing downloaded (§16), self-disposing.
 */

const CSS = `
@keyframes fgdoor-inL{from{transform:translateX(-101%)}to{transform:none}}
@keyframes fgdoor-inR{from{transform:translateX(101%)}to{transform:none}}
@keyframes fgdoor-outL{to{transform:translateX(-101%)}}
@keyframes fgdoor-outR{to{transform:translateX(101%)}}
@keyframes fgdoor-shake{0%,100%{transform:none}20%{transform:translate(9px,5px)}40%{transform:translate(-8px,-4px)}60%{transform:translate(6px,-5px)}80%{transform:translate(-4px,3px)}}
@keyframes fgdoor-vs{from{opacity:0;transform:translate(-50%,-50%) scale(2.6)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}
@keyframes fggoal-band{0%{transform:translateX(-120%) skewX(-14deg)}22%{transform:translateX(-6%) skewX(-14deg)}78%{transform:translateX(6%) skewX(-14deg)}100%{transform:translateX(120%) skewX(-14deg)}}
@keyframes fggoal-text{0%{transform:translateX(-130%) skewX(-8deg)}22%{transform:translateX(-4%) skewX(-8deg)}78%{transform:translateX(4%) skewX(-8deg)}100%{transform:translateX(130%) skewX(-8deg)}}
.fgdoors{position:absolute;inset:0;z-index:60;overflow:hidden;font-family:system-ui,sans-serif;pointer-events:none}
.fgdoors.shake{animation:fgdoor-shake .38s ease both}
.fgdoor{position:absolute;top:0;bottom:0;width:50.5%;display:flex;align-items:center;
  animation-duration:.55s;animation-timing-function:cubic-bezier(.7,0,.9,1);animation-fill-mode:both}
.fgdoor b{font-size:clamp(30px,6vw,64px);font-weight:900;letter-spacing:.08em;color:#fff;
  text-shadow:0 4px 18px rgba(0,0,0,.6);width:100%;padding:0 8%}
.fgdoor.L{left:0;animation-name:fgdoor-inL;border-right:3px solid rgba(255,255,255,.25)}
.fgdoor b{text-align:center;padding:0 14% 0 6%}
.fgdoor.R b{padding:0 6% 0 14%}
.fgdoor.R{right:0;animation-name:fgdoor-inR;border-left:3px solid rgba(255,255,255,.25)}
.fgdoors.open .fgdoor.L{animation:fgdoor-outL .6s cubic-bezier(.5,0,1,.5) both}
.fgdoors.open .fgdoor.R{animation:fgdoor-outR .6s cubic-bezier(.5,0,1,.5) both}
.fgvs{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:2;
  font:900 clamp(40px,8vw,90px) system-ui;color:#ffd94a;-webkit-text-stroke:2px #141405;
  text-shadow:0 6px 24px rgba(0,0,0,.7);animation:fgdoor-vs .3s .5s cubic-bezier(.2,2,.4,1) both}
.fgdoors.open .fgvs{animation:none;opacity:0;transform:translate(-50%,-50%) scale(1.4);transition:opacity .25s,transform .25s}
.fggoal{position:absolute;inset:0;z-index:55;overflow:hidden;pointer-events:none;display:flex;align-items:center}
.fggoal i{position:absolute;left:0;right:0;height:26%;top:37%;
  background:linear-gradient(90deg,transparent,VAR 30%,VAR 70%,transparent);opacity:.9;
  animation:fggoal-band 1.35s cubic-bezier(.5,0,.5,1) both}
.fggoal b{position:relative;width:100%;text-align:center;font:900 clamp(60px,13vw,150px)/1 system-ui;
  font-style:italic;letter-spacing:.14em;color:#fff;-webkit-text-stroke:2px rgba(0,0,0,.35);
  text-shadow:0 8px 30px rgba(0,0,0,.55);animation:fggoal-text 1.35s cubic-bezier(.5,0,.5,1) both}
`;

let styleEl: HTMLStyleElement | null = null;
function ensureCss(): void {
  if (styleEl?.isConnected) return;
  styleEl = document.createElement('style');
  styleEl.textContent = CSS.replace(/VAR/g, 'var(--fgband)');
  document.head.appendChild(styleEl);
}

/**
 * Doors slam shut over the pitch, shake, then rip open onto the game.
 *
 * `isReady` (default: always) gates the OPEN, not the slam — the shake at
 * 560ms always fires on schedule, so the door still reads as a deliberate
 * broadcast beat rather than a stall. If the pitch behind the doors isn't
 * ready at the scheduled 2050ms reveal (the cast — character models — can
 * still be streaming in for several seconds after this mounts; see
 * castPlayerVisual.ts's own `ready` gate, which draws nothing at all until
 * every model is in), the doors simply stay shut and are polled every 150ms
 * until it flips true, THEN open on the same 650ms-to-dispose timing as the
 * ready case. This is what actually closes the empty-pitch gap: the flyby
 * camera underneath (ui/matchIntro.ts) is running the whole time regardless,
 * but the doors are opaque, so none of that ever reaches the screen until
 * there is something worth revealing.
 */
export function playDoorsIntro(root: HTMLElement, home: string, away: string,
  homeColor: string, awayColor: string, onDone?: () => void,
  isReady?: () => boolean): { dispose(): void } {
  ensureCss();
  const el = document.createElement('div');
  el.className = 'fgdoors';
  const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => `&#${c.charCodeAt(0)};`);
  el.innerHTML =
    `<div class="fgdoor L" style="background:linear-gradient(105deg,${esc(homeColor)},#10161f 140%)"><b>${esc(home)}</b></div>` +
    `<div class="fgdoor R" style="background:linear-gradient(255deg,${esc(awayColor)},#10161f 140%)"><b>${esc(away)}</b></div>` +
    `<div class="fgvs">VS</div>`;
  root.appendChild(el);
  const timers: number[] = [];
  let pollTimer: number | null = null;
  const reveal = (): void => {
    timers.push(window.setTimeout(() => el.classList.add('open'), 0));
    timers.push(window.setTimeout(() => { el.remove(); onDone?.(); }, 650));
  };
  timers.push(window.setTimeout(() => el.classList.add('shake'), 560)); // the SLAM
  timers.push(window.setTimeout(() => {
    if (!isReady || isReady()) {
      reveal();
      return;
    }
    pollTimer = window.setInterval(() => {
      if (!isReady!()) return;
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      reveal();
    }, 150);
  }, 2050)); // hold, then reveal — or wait for the pitch behind it to be ready
  return {
    dispose: () => {
      for (const t of timers) clearTimeout(t);
      if (pollTimer) clearInterval(pollTimer);
      el.remove();
    },
  };
}

/** The goal wipe: a club-colour band and a huge italic GOAL sweep through. */
export function playGoalWipe(root: HTMLElement, teamColor: string): { dispose(): void } {
  ensureCss();
  const el = document.createElement('div');
  el.className = 'fggoal';
  el.style.setProperty('--fgband', teamColor);
  el.innerHTML = `<i></i><b>GOAL!</b>`;
  root.appendChild(el);
  const t = window.setTimeout(() => el.remove(), 1400);
  return { dispose: () => { clearTimeout(t); el.remove(); } };
}
